import { useState } from 'react';
import { Code2, Crosshair, Plus } from 'lucide-react';
import { I18n } from '@/i18n/I18n';
import { Check, readRulesFile } from '@/reports/bundles/expectations';
import { FixtureResult } from '@/reports/bundles/types';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import { Prop } from '../inputs/builderControls';
import { CheckEditor, CheckRow, ChecksUnread } from './CheckEditor';
import { resultOf } from './FixtureView';
import { baseName } from './designerChrome';

const VERDICTS = ['', 'passed', 'failed', 'incomplete'];

export const writeRules = (rules: { description?: string; when?: { verdict?: string }; checks: Check[] }) => `${JSON.stringify(rules, null, 2)}\n`;

/**
 * A rules file in `tests/`, shown as what it is: checks every fixture is held
 * to — or every fixture that must come to one verdict — each with how it came
 * out on each fixture at the last save. The Code view is the same file.
 */
export function RulesView({ path, text, results, onChange, onOpenCode, onPick, onOpenFile }: {
  path: string;
  text: string;
  /** every fixture's result as last saved, the printed document's checks beside the compile's */
  results: Array<FixtureResult & { pending?: boolean }>;
  onChange: (text: string) => void;
  onOpenCode: () => void;
  onPick?: () => void;
  onOpenFile: (path: string) => void;
}) {
  const [adding, setAdding] = useState(false);
  const { rules, problems } = readRulesFile(text);
  if (!rules) {
    return (
      <div className="flex flex-col items-start gap-2 p-[1.5rem]">
        <p className="text-destructive">{I18n.get('rulesUnreadable', { file: path, problem: problems.join(' ') })}</p>
        <Button variant="outline" onClick={onOpenCode}><Code2 /> {I18n.get('rv2OpenInCode')}</Button>
      </div>
    );
  }
  // written over the file as it is, not as read: a check that could not be read stays for the author to fix in code
  const raw = JSON.parse(text) as Record<string, unknown>;
  const rawChecks: unknown[] = Array.isArray(raw.checks) ? raw.checks : [];
  const update = (change: Partial<typeof rules>) => onChange(writeRules({ ...raw, ...change } as Parameters<typeof writeRules>[0]));
  // with a check that could not be read, the list here is not the file's: checks are added and removed in code until it reads
  const locked = problems.length > 0;
  const applies = results.filter((r) => !rules.when?.verdict || r.expected?.verdict === rules.when.verdict);

  /** How a check came out over the fixtures it applies to: held on all, or where it missed and why. */
  const outcome = (check: Check) => {
    const made = applies.map((r) => ({ file: r.file, result: resultOf(r.checks, path, check), pending: !!r.pending }));
    const missed = made.filter((m) => m.result && !m.result.ok);
    const held = made.filter((m) => m.result?.ok).length;
    const pending = made.some((m) => !m.result && m.pending);
    const first = missed[0]?.result;
    const result = made.length === 0 || made.every((m) => !m.result) ? undefined : missed.length ? first : { check, from: path, ok: true };
    const detail = applies.length === 0
      ? I18n.get('rulesNoFixtures')
      : missed.length
        ? I18n.get('rulesMissedOn', { files: missed.map((m) => baseName(m.file)).join(', '), held: String(held), total: String(applies.length) })
        : held ? I18n.get('rulesHeldOn', { held: String(held), total: String(applies.length) }) : undefined;
    return { result, detail, pending: pending && !result };
  };

  return (
    <div className="@container h-full overflow-y-auto bg-muted/30">
      <div className="mx-auto flex max-w-[52rem] flex-col gap-[1rem] px-[1.5rem] py-[1.25rem]">
        <p className="text-xs text-muted-foreground">{I18n.get('rulesNote')}</p>
        <section aria-label={I18n.get('rulesAbout')} className="grid gap-[0.75rem] rounded-xl border-[1px] bg-background p-[1rem] @xl:grid-cols-2">
          <Prop label={I18n.get('rulesDescription')}>
            <Input aria-label={I18n.get('rulesDescription')} value={rules.description ?? ''} onChange={(e) => update({ description: e.target.value || undefined })} />
          </Prop>
          <Prop label={I18n.get('rulesAppliesTo')}>
            <select aria-label={I18n.get('rulesAppliesTo')} value={rules.when?.verdict ?? ''} onChange={(e) => update({ when: e.target.value ? { verdict: e.target.value } : undefined })} className="h-8 rounded-lg border-[1px] border-input bg-background px-2 text-sm">
              {VERDICTS.map((v) => <option key={v} value={v}>{v ? I18n.get('rulesAppliesToVerdict', { verdict: I18n.get(`verdict_${v}`) }) : I18n.get('rulesAppliesToEvery')}</option>)}
            </select>
          </Prop>
          <p className="text-xs text-muted-foreground @xl:col-span-2">
            {applies.length ? I18n.get('rulesHolds', { files: applies.map((r) => baseName(r.file)).join(', ') }) : I18n.get('rulesNoFixtures')}
          </p>
        </section>

        <section aria-label={I18n.get('rulesChecks')} className="flex flex-col rounded-xl border-[1px] bg-background">
          <div className="flex flex-wrap items-center gap-2 px-[1rem] pt-[0.75rem] pb-2">
            <h3 className="mr-auto text-[0.7rem] font-semibold tracking-wide text-muted-foreground uppercase">{I18n.get('rulesChecks')}</h3>
            {onPick && <Button variant="outline" size="xs" onClick={onPick} disabled={locked}><Crosshair /> {I18n.get('fixturePickFromPreview')}</Button>}
            <Button variant="ghost" size="xs" onClick={() => setAdding(true)} disabled={adding || locked}><Plus /> {I18n.get('fixtureAddCheck')}</Button>
          </div>
          {rules.checks.length === 0 && !adding && <p className="px-[1rem] pb-[0.75rem] text-sm text-muted-foreground">{I18n.get('rulesNoChecks')}</p>}
          {rules.checks.length > 0 && (
            <ul aria-label={I18n.get('rulesChecks')} className="divide-y border-t-[1px]">
              {rules.checks.map((check, i) => {
                const { result, detail, pending } = outcome(check);
                return <CheckRow key={i} check={check} result={result} pending={pending} detail={detail} onRemove={locked ? undefined : () => update({ checks: rawChecks.filter((_, j) => j !== i) as Check[] })} />;
              })}
            </ul>
          )}
          {adding && (
            <div className="border-t-[1px] p-[0.75rem]">
              <CheckEditor onCancel={() => setAdding(false)} onAdd={(check) => { update({ checks: [...rawChecks, check] as Check[] }); setAdding(false); }} />
            </div>
          )}
          {locked && <ChecksUnread problems={problems} onOpenCode={onOpenCode} />}
        </section>
        {applies.length > 0 && (
          <p className="flex flex-wrap gap-x-2 text-xs text-muted-foreground">
            {applies.map((r) => <button key={r.file} type="button" className="font-mono hover:text-foreground hover:underline" onClick={() => onOpenFile(r.file)}>{baseName(r.file)}</button>)}
          </p>
        )}
      </div>
    </div>
  );
}
